import {
  IReportCustomFieledConfig,
  ReportCustomFieldType,
  ReportMedia,
} from "@shared/lib/types/reports";
import { formatData } from "@shared/lib/utils/formatData";
import { Button, Input, Tooltip } from "@shared/ui";
import InputNumber from "@shared/ui/InputNumber";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Close } from "@shared/ui/icons";
import { Flex } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Check } from "lucide-react";
import styles from "./ReportMediaTable.module.scss";

export type ReportDataFieldEditState = {
  reportMediaId: string;
  key: string;
  value?: string | number;
};

type BuildParams = {
  fields: IReportCustomFieledConfig[];
  editFieldProps: ReportDataFieldEditState;
  setEditFieldProps: React.Dispatch<
    React.SetStateAction<ReportDataFieldEditState>
  >;
  onSave: (payload: {
    itemId: string;
    dataField: { [key: string]: string | number };
  }) => void;
  isSaving: boolean;
};

function isNumberField(field: IReportCustomFieledConfig): boolean {
  return field.type === ReportCustomFieldType.NUMBER;
}

export function buildReportCustomFieldColumns(
  params: BuildParams
): ColumnsType<ReportMedia> {
  const { fields, editFieldProps, setEditFieldProps, onSave, isSaving } =
    params;

  return fields.map((field) => ({
    title: (
      <Tooltip title={field.displayName || field.key}>
        <span>{field.displayName || field.key}</span>
      </Tooltip>
    ),
    width: 140,
    dataIndex: field.key,
    key: `report-cf-${field.key}`,
    render: (_: unknown, record: ReportMedia) => {
      const status = record.mediaDataStatus || "Pending";
      if (status === "Pending") {
        return null;
      }

      const dynamic = record.dynamicFields || {};
      const hasKey = Object.prototype.hasOwnProperty.call(dynamic, field.key);
      const value = dynamic[field.key];

      const isEditingField =
        editFieldProps.key === field.key &&
        record._id === editFieldProps.reportMediaId;

      const numberField = isNumberField(field);

      if (!hasKey) {
        return (
          <div
            className={styles.editableDataFieldEmpty}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setEditFieldProps({
                value: numberField ? 0 : "",
                key: field.key,
                reportMediaId: record._id,
              });
            }}
          >
            —
          </div>
        );
      }

      if (isEditingField) {
        const handleSave = () => {
          const v = editFieldProps.value;
          const payload = numberField
            ? { [field.key]: v === "" || v === undefined ? 0 : Number(v) }
            : { [field.key]: (v === undefined ? "" : String(v)) as string };
          onSave({ itemId: record._id, dataField: payload });
        };

        const handleCancel = (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          setEditFieldProps({ value: undefined, key: "", reportMediaId: "" });
        };

        const suffix = (
          <Flex gap={2}>
            <Button
              type="text"
              size="small"
              icon={<Check size={10} />}
              loading={isSaving}
              disabled={isSaving}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSave();
              }}
              className={styles.editActionButton}
            />
            <Button
              type="text"
              size="small"
              icon={<Close />}
              onClick={handleCancel}
              className={styles.editActionButton}
            />
          </Flex>
        );

        if (numberField) {
          return (
            <InputNumber
              value={
                editFieldProps.value === "" ||
                editFieldProps.value === undefined
                  ? undefined
                  : Number(editFieldProps.value)
              }
              onChange={(val) => {
                setEditFieldProps((prev) => ({
                  ...prev,
                  value: val === null || val === undefined ? 0 : val,
                }));
              }}
              onPressEnter={handleSave}
              size="small"
              className={styles.customFieldInput}
              addonAfter={suffix}
              disabled={isSaving}
            />
          );
        }

        return (
          <Input
            value={
              editFieldProps.value === undefined ||
              editFieldProps.value === null
                ? ""
                : String(editFieldProps.value)
            }
            onChange={(e) =>
              setEditFieldProps((prev) => ({
                ...prev,
                value: e.target.value,
              }))
            }
            onPressEnter={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            size="small"
            className={styles.customFieldInput}
            addonAfter={suffix}
            disabled={isSaving}
          />
        );
      }

      return (
        <div
          className={styles.editableDataFieldCell}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setEditFieldProps({
              value: numberField
                ? value === undefined || value === ""
                  ? 0
                  : Number(value)
                : ((value as string | number | undefined) ?? ""),
              key: field.key,
              reportMediaId: record._id,
            });
          }}
        >
          {numberField ? (
            value !== undefined && value !== null && value !== "" ? (
              <TextWithTooltip
                text={formatData(
                  Number(Number(value).toFixed(4)),
                  "0.00"
                ).toString()}
              />
            ) : (
              <span className={styles.editableDataFieldEmpty}>0</span>
            )
          ) : value !== undefined && value !== null && String(value) !== "" ? (
            <TextWithTooltip text={String(value)} limit={32} />
          ) : (
            <span className={styles.editableDataFieldEmpty}>—</span>
          )}
        </div>
      );
    },
  }));
}
